/**
 * Admin Models Page
 *
 * Manage LLM providers and models configuration.
 */

import { A } from '@solidjs/router';
import { type Component, createResource, createSignal, For, Show } from 'solid-js';
import {
  type AdminModelsConfig,
  fetchAdminModelsConfig,
  resetAdminModelsConfig,
  updateAdminModelsConfig,
} from '../services/api';

type ProviderConfig = AdminModelsConfig['providers'][0];
type ModelConfig = ProviderConfig['models'][0];

const AdminModels: Component = () => {
  const [config, { refetch }] = createResource(fetchAdminModelsConfig);
  const [editingProvider, setEditingProvider] = createSignal<string | null>(null);
  const [editingModel, setEditingModel] = createSignal<{ providerId: string; modelId: string } | null>(null);
  const [saving, setSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [success, setSuccess] = createSignal<string | null>(null);

  // Local state for editing
  const [localProviders, setLocalProviders] = createSignal<ProviderConfig[]>([]);

  // Initialize local state when config loads
  const initLocalState = () => {
    const c = config();
    if (c) {
      setLocalProviders(JSON.parse(JSON.stringify(c.providers)));
    }
  };

  // Add new model to a provider
  const addModel = (providerId: string) => {
    setLocalProviders((prev) =>
      prev.map((p) => {
        if (p.id === providerId) {
          return {
            ...p,
            models: [
              ...p.models,
              {
                id: `new-model-${Date.now()}`,
                displayName: 'New Model',
                description: 'Description',
                status: 'ga' as const,
              },
            ],
          };
        }
        return p;
      }),
    );
  };

  // Remove model from a provider
  const removeModel = (providerId: string, modelId: string) => {
    setLocalProviders((prev) =>
      prev.map((p) => {
        if (p.id === providerId) {
          return {
            ...p,
            models: p.models.filter((m) => m.id !== modelId),
          };
        }
        return p;
      }),
    );
  };

  // Update model
  const updateModel = (providerId: string, modelId: string, updates: Partial<ModelConfig>) => {
    setLocalProviders((prev) =>
      prev.map((p) => {
        if (p.id === providerId) {
          return {
            ...p,
            models: p.models.map((m) => (m.id === modelId ? { ...m, ...updates } : m)),
          };
        }
        return p;
      }),
    );
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await updateAdminModelsConfig(localProviders());
      setSuccess('Models config saved successfully!');
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = async () => {
    if (!confirm('Reset to static defaults? This will remove all custom changes.')) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await resetAdminModelsConfig();
      setSuccess('Reset to defaults successfully!');
      refetch();
      // Re-initialize local state after refetch
      setTimeout(initLocalState, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="max-w-4xl">
      {/* Header */}
      <div class="flex items-center justify-between mb-6">
        <div>
          <div class="flex items-center gap-2 text-sm text-gray-400 mb-2">
            <A href="/settings" class="hover:text-white">
              Settings
            </A>
            <span>/</span>
            <span class="text-white">LLM Models</span>
          </div>
          <h1 class="text-2xl font-bold">LLM Models Configuration</h1>
          <p class="text-gray-400 text-sm mt-1">
            Manage available LLM providers and models for agent execution.
          </p>
        </div>
      </div>

      {/* Status Messages */}
      <Show when={error()}>
        <div class="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
          <p class="text-red-400 text-sm">{error()}</p>
        </div>
      </Show>

      <Show when={success()}>
        <div class="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg">
          <p class="text-green-400 text-sm">{success()}</p>
        </div>
      </Show>

      {/* Loading State */}
      <Show when={config.loading}>
        <div class="flex items-center justify-center py-12">
          <div class="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Show>

      {/* Config Info */}
      <Show when={config()}>
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-4 mb-6">
          <div class="flex items-center justify-between">
            <div>
              <span class="text-sm text-gray-400">Source: </span>
              <span
                class={`text-sm font-medium ${config()?.source === 'database' ? 'text-green-400' : 'text-yellow-400'}`}
              >
                {config()?.source === 'database' ? 'Database (customized)' : 'Static (defaults)'}
              </span>
              <Show when={config()?.updatedAt}>
                <span class="text-sm text-gray-500 ml-4">
                  Updated: {new Date(config()!.updatedAt!).toLocaleString()}
                </span>
              </Show>
            </div>
            <div class="flex gap-2">
              <button
                onClick={() => {
                  initLocalState();
                  setEditingProvider('all');
                }}
                class="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors"
              >
                Edit Config
              </button>
              <button
                onClick={handleReset}
                disabled={saving()}
                class="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors disabled:opacity-50"
              >
                Reset to Defaults
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* View Mode: Show current config */}
      <Show when={config() && !editingProvider()}>
        <div class="space-y-4">
          <For each={config()?.providers}>
            {(provider) => (
              <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                <div class="flex items-center gap-3 mb-4">
                  <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                    {provider.name.charAt(0)}
                  </div>
                  <div>
                    <h3 class="text-lg font-medium text-white">{provider.name}</h3>
                    <p class="text-sm text-gray-400">{provider.description}</p>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <For each={provider.models}>
                    {(model) => (
                      <div class="bg-gray-900/50 border border-gray-600 rounded-lg p-3">
                        <div class="flex items-center justify-between mb-1">
                          <span class="font-medium text-white">{model.displayName}</span>
                          <span
                            class={`px-2 py-0.5 text-xs rounded-full ${
                              model.status === 'ga'
                                ? 'bg-green-500/20 text-green-400'
                                : model.status === 'preview'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-gray-500/20 text-gray-400'
                            }`}
                          >
                            {model.status}
                          </span>
                        </div>
                        <p class="text-sm text-gray-400">{model.description}</p>
                        <p class="text-xs text-gray-500 mt-1 font-mono">{model.id}</p>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Edit Mode */}
      <Show when={editingProvider()}>
        <div class="space-y-4">
          <For each={localProviders()}>
            {(provider) => (
              <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                <div class="flex items-center justify-between mb-4">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                      {provider.name.charAt(0)}
                    </div>
                    <div>
                      <h3 class="text-lg font-medium text-white">{provider.name}</h3>
                      <p class="text-xs text-gray-500 font-mono">{provider.id}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => addModel(provider.id)}
                    class="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors"
                  >
                    + Add Model
                  </button>
                </div>

                <div class="space-y-3">
                  <For each={provider.models}>
                    {(model) => (
                      <div class="bg-gray-900/50 border border-gray-600 rounded-lg p-4">
                        <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div>
                            <label class="block text-xs text-gray-400 mb-1">Model ID</label>
                            <input
                              type="text"
                              value={model.id}
                              onInput={(e) =>
                                updateModel(provider.id, model.id, { id: e.currentTarget.value })
                              }
                              class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <label class="block text-xs text-gray-400 mb-1">Display Name</label>
                            <input
                              type="text"
                              value={model.displayName}
                              onInput={(e) =>
                                updateModel(provider.id, model.id, {
                                  displayName: e.currentTarget.value,
                                })
                              }
                              class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <label class="block text-xs text-gray-400 mb-1">Status</label>
                            <select
                              value={model.status}
                              onChange={(e) =>
                                updateModel(provider.id, model.id, {
                                  status: e.currentTarget.value as ModelConfig['status'],
                                })
                              }
                              class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="ga">GA</option>
                              <option value="preview">Preview</option>
                              <option value="deprecated">Deprecated</option>
                            </select>
                          </div>
                          <div class="flex items-end">
                            <button
                              onClick={() => removeModel(provider.id, model.id)}
                              class="px-3 py-2 text-sm bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                        <div class="mt-3">
                          <label class="block text-xs text-gray-400 mb-1">Description</label>
                          <input
                            type="text"
                            value={model.description}
                            onInput={(e) =>
                              updateModel(provider.id, model.id, {
                                description: e.currentTarget.value,
                              })
                            }
                            class="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>

          {/* Save/Cancel buttons */}
          <div class="flex gap-3 justify-end pt-4 border-t border-gray-700">
            <button
              onClick={() => setEditingProvider(null)}
              class="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving()}
              class="px-6 py-2 text-sm bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg transition-all disabled:opacity-50 shadow-lg shadow-purple-500/20"
            >
              {saving() ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
};

export default AdminModels;
